/**
 * http.ts — the ENTIRE browser-side auth story for the Vendor Portal.
 * =====================================================================
 *
 * This is the ONLY module in the portal that talks to the network or touches
 * auth. Everything else goes through `client.ts` (the typed wrappers), which
 * goes through here. If you are reviewing the portal's security posture, this
 * file plus `client.ts` is the whole surface.
 *
 * The locked architecture (Path B, same-origin) makes this small on purpose:
 *
 *  - SAME-ORIGIN. The SPA is served at `/` and the API at `/api/*` by ONE
 *    CloudFront distribution (the MangoFrontend stack), which forwards the
 *    portal Host header to the Django ALB. So to the browser the API is just
 *    "this site". No CORS, no preflight, no `Access-Control-*`.
 *
 *  - SESSION COOKIE (httpOnly). Django sets a first-party `sessionid` cookie on
 *    login (`/accounts/login/`). Because the API is same-origin, the browser
 *    attaches it automatically — we never read, store, or forward it. It is
 *    httpOnly, so it is unreachable from JS (XSS cannot exfiltrate it). We only
 *    set `credentials: 'same-origin'` so fetch includes it.
 *
 *  - CSRF. Django's CSRF protection requires unsafe requests (POST/PUT/PATCH/
 *    DELETE) to echo the `csrftoken` cookie in an `X-CSRFToken` header. That
 *    cookie is NOT httpOnly (by design — the SPA must read it), so we read it
 *    and set the header. This is the only auth "work" the browser does.
 *
 *  - NO TOKENS, NO localStorage, NO secrets. There is deliberately nothing to
 *    leak here.
 *
 * On 401/403 we show the branded sign-in gate (auth-gate.ts) rather than silently
 * redirecting. DRF returns 403 for BOTH "not logged in" and "logged in but not a
 * Provider", so an auto-redirect to login looped forever for the second case; the
 * gate cannot loop (it navigates only on an explicit Sign in / Sign out click) and
 * gives a wrong-account user an obvious way out.
 */
import { showAuthGate, hideAuthGate } from "../auth-gate";

/** Read a cookie value by name. Returns null if absent. */
function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const c = part.trim();
    if (c.startsWith(prefix)) {
      return decodeURIComponent(c.slice(prefix.length));
    }
  }
  return null;
}

/** HTTP methods that mutate state and therefore require the CSRF header. */
const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** Raised when the API returns a non-2xx that is NOT an auth redirect. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `API request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export interface RequestOptions {
  method?: string;
  /** JSON body — serialised automatically and sent as application/json. */
  json?: unknown;
  /**
   * Multipart body — a FormData (e.g. an inventory/FMV CSV upload). Sent as-is so
   * the browser sets `multipart/form-data` with the correct boundary; we never
   * set Content-Type for it. This is purely a body-encoding branch — the auth
   * story below (same-origin cookie + CSRF header + 401/403 → login) is identical
   * for JSON and multipart requests.
   */
  form?: FormData;
  /** URLSearchParams appended to the path (for list filters/pagination). */
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
}

/**
 * The single same-origin fetch primitive. All API access funnels through here.
 *
 * @param path  An absolute API path, e.g. "/api/v1/provider/work-orders/".
 *              Always same-origin; never a full URL, never another host.
 */
export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = (opts.method ?? "GET").toUpperCase();

  let url = path;
  if (opts.query) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined) params.set(k, String(v));
    }
    const qs = params.toString();
    if (qs) url += (url.includes("?") ? "&" : "?") + qs;
  }

  const headers: Record<string, string> = { Accept: "application/json" };
  const init: RequestInit = {
    method,
    headers,
    // Send the same-origin session cookie. Never "include" (that is for
    // cross-origin, which we do not do and must not enable).
    credentials: "same-origin",
  };
  if (opts.signal) init.signal = opts.signal;

  if (opts.form !== undefined) {
    // Multipart: do NOT set Content-Type — the browser adds the boundary itself.
    init.body = opts.form;
  } else if (opts.json !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(opts.json);
  }

  if (UNSAFE_METHODS.has(method)) {
    const csrf = readCookie("csrftoken");
    if (csrf) headers["X-CSRFToken"] = csrf;
    // If the cookie is missing the request will 403; we let the server be the
    // authority and route that through the auth-redirect path below.
  }

  const res = await fetch(url, init);

  // 401/403 handling — show the branded sign-in gate (never auto-redirect, so we
  // never loop). DRF SessionAuthentication returns 403 (not 401) for an
  // UNauthenticated request AND for "logged in but not a Provider"; the gate
  // covers both: the user explicitly chooses Sign in or Sign out. We still throw
  // so the calling screen's promise settles (its error is harmless — the gate
  // overlays the screen).
  if (res.status === 401 || res.status === 403) {
    showAuthGate();
    const body = await safeParse(res);
    throw new ApiError(res.status, body, "Provider portal access required — sign in to continue.");
  }

  if (!res.ok) {
    const body = await safeParse(res);
    throw new ApiError(res.status, body);
  }

  // Success — an authenticated request went through; ensure the gate is hidden.
  hideAuthGate();
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

async function safeParse(res: Response): Promise<unknown> {
  try {
    const text = await res.text();
    return text ? JSON.parse(text) : null;
  } catch {
    return null;
  }
}
